import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "../admin/authorization.functions";

function isNewKey(v: string) {
  return v.startsWith("sb_publishable_") || v.startsWith("sb_secret_");
}

function publicClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error("supabase_env_missing");
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (isNewKey(key) && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export interface HomepageCoupon {
  id: string;
  code: string;
  name: string;
  description: string | null;
  type: string;
  value: number;
}

export interface HomepageCouponDisplay {
  enabled: boolean;
  mode: "featured" | "rotation";
  coupons: HomepageCoupon[];
}

/** Pure selection rule, exported for focused tests. */
export function pickDisplayCoupons(
  settings: { enabled: boolean; mode: string; featured_coupon_id: string | null } | null,
  eligible: HomepageCoupon[],
): HomepageCouponDisplay {
  if (!settings?.enabled) return { enabled: false, mode: "featured", coupons: [] };
  if (settings.mode === "featured" && settings.featured_coupon_id) {
    return {
      enabled: true,
      mode: "featured",
      coupons: eligible.filter((c) => c.id === settings.featured_coupon_id),
    };
  }
  return { enabled: true, mode: "rotation", coupons: eligible };
}

/**
 * Public read: returns the homepage coupon display setting plus only the
 * coupons currently eligible for promotion (active, within their validity
 * window, non-demo). Checkout remains authoritative for actual discounts.
 */
export const getHomepageCouponDisplay = createServerFn({ method: "GET" }).handler(
  async (): Promise<HomepageCouponDisplay> => {
    const supabase = publicClient();
    const { data: settings } = await supabase
      .from("homepage_coupon_display")
      .select("enabled, mode, featured_coupon_id")
      .eq("id", 1)
      .maybeSingle();

    if (!settings?.enabled) return { enabled: false, mode: "featured", coupons: [] };

    const now = new Date().toISOString();
    const { data: coupons, error } = await supabase
      .from("mp2_coupons")
      .select("id, code, name, description, type, value")
      .eq("is_active", true)
      .eq("is_demo", false)
      .or(`start_at.is.null,start_at.lte.${now}`)
      .or(`end_at.is.null,end_at.gte.${now}`)
      .order("priority", { ascending: false });
    if (error) throw new Error(`homepage_coupons:${error.message}`);

    return pickDisplayCoupons(settings, (coupons ?? []) as HomepageCoupon[]);
  },
);

const settingsInput = z.object({
  enabled: z.boolean(),
  mode: z.enum(["featured", "rotation"]),
  featured_coupon_id: z.string().uuid().nullable(),
});

export const getHomepageCouponSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase);
    const { data, error } = await context.supabase
      .from("homepage_coupon_display")
      .select("enabled, mode, featured_coupon_id")
      .eq("id", 1)
      .maybeSingle();
    if (error) throw new Error(`getHomepageCouponSettings:${error.message}`);
    return data ?? { enabled: false, mode: "featured" as const, featured_coupon_id: null };
  });

export const updateHomepageCouponSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => settingsInput.parse(raw))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase);
    const { error } = await context.supabase
      .from("homepage_coupon_display")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", 1);
    if (error) throw new Error(`updateHomepageCouponSettings:${error.message}`);
    return { ok: true as const };
  });

// ---- Checkout coupon display (independent settings) ----

export interface CheckoutCouponDisplay extends HomepageCouponDisplay {
  placement: "above_input" | "order_summary";
}

async function fetchEligibleCoupons(supabase: ReturnType<typeof publicClient>) {
  const now = new Date().toISOString();
  const { data: coupons, error } = await supabase
    .from("mp2_coupons")
    .select("id, code, name, description, type, value")
    .eq("is_active", true)
    .eq("is_demo", false)
    .or(`start_at.is.null,start_at.lte.${now}`)
    .or(`end_at.is.null,end_at.gte.${now}`)
    .order("priority", { ascending: false });
  if (error) throw new Error(`checkout_coupons:${error.message}`);
  return (coupons ?? []) as HomepageCoupon[];
}

/** Public read: checkout promo display setting plus eligible coupons only. */
export const getCheckoutCouponDisplay = createServerFn({ method: "GET" }).handler(
  async (): Promise<CheckoutCouponDisplay> => {
    const supabase = publicClient();
    const { data: settings } = await supabase
      .from("checkout_coupon_display")
      .select("enabled, placement, mode, featured_coupon_id")
      .eq("id", 1)
      .maybeSingle();

    if (!settings?.enabled) {
      return { enabled: false, placement: "above_input", mode: "featured", coupons: [] };
    }
    const picked = pickDisplayCoupons(settings, await fetchEligibleCoupons(supabase));
    return { ...picked, placement: settings.placement as "above_input" | "order_summary" };
  },
);

const checkoutSettingsInput = z.object({
  enabled: z.boolean(),
  placement: z.enum(["above_input", "order_summary"]),
  mode: z.enum(["featured", "rotation"]),
  featured_coupon_id: z.string().uuid().nullable(),
});

export const getCheckoutCouponSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase);
    const { data, error } = await context.supabase
      .from("checkout_coupon_display")
      .select("enabled, placement, mode, featured_coupon_id")
      .eq("id", 1)
      .maybeSingle();
    if (error) throw new Error(`getCheckoutCouponSettings:${error.message}`);
    return (
      data ?? {
        enabled: false,
        placement: "above_input" as const,
        mode: "featured" as const,
        featured_coupon_id: null,
      }
    );
  });

export const updateCheckoutCouponSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) => checkoutSettingsInput.parse(raw))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase);
    const { error } = await context.supabase
      .from("checkout_coupon_display")
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq("id", 1);
    if (error) throw new Error(`updateCheckoutCouponSettings:${error.message}`);
    return { ok: true as const };
  });
